import Parse from 'parse'
import { setUser as setSentryUser, setContext as setSentryContext } from '@sentry/react'
import { showToast } from '../../client/uiComponents/notifications/toastState'
import { getCartContent, UPDATE_CART_ON_LOGIN } from '../cart/actions'
import { analyticsLogin } from '../analytics/actions'
import { cacheEntities } from '../entities/actions'
import { activeAbTests, getABTestFraction } from '../../common/analytics/abTest'
import { internationalBankAccountFields } from '../region/config'
import { clearCloudCache } from '../lib/parseTools'
import { getApolloClient } from '../../client/apollo/apolloClientSingleton'
import {
  refreshUserIdTracking,
  initializeEmailMarketingConsent
} from '../../client/components/dataConsent'
import { PERMISSION_TYPE_EMAIL_MARKETING } from './dataHandling'
import { getCurrentParseUser } from './currentUser'
import { setCurrentUser, becomeUser, logoutCurrentUser } from './userActions'

const resetAuthAndRefresh = () => {
  logoutCurrentUser().catch(() => window.location.reload(true))
}

const handleInvalidSessionToken = (error) => {
  if (error.code === Parse.Error.INVALID_SESSION_TOKEN) {
    resetAuthAndRefresh()
  } else {
    throw error
  }
}

export function tryLoggedIn() {
  return (dispatch) => {
    const parseUser = getCurrentParseUser()
    if (parseUser) {
      const prevUser = JSON.stringify(parseUser.toJSON())
      return parseUser
        .fetch()
        .then(() => {
          dispatch(afterLoggedInHook(parseUser))
          const newUser = JSON.stringify(parseUser.toJSON())
          const outdatedStateInLocalStorage = prevUser !== newUser
          if (outdatedStateInLocalStorage) setCurrentUser(parseUser)
        })
        .catch(handleInvalidSessionToken)
    }
  }
}

export const afterSignupParse = ({ user }) => {
  return (dispatch) => {
    dispatch({ type: UPDATE_CART_ON_LOGIN })
    clearCloudCache()
    dispatch(afterLoggedInHook(user))
  }
}

export const afterLoggedInHook = (user) => {
  return async (dispatch) => {
    await initializeEmailMarketingConsent()
    await refreshUserIdTracking()
    setSentryUser({ id: user?.id })
    setSentryContext('A/B Test', {
      clientFraction: getABTestFraction(),
      activeTests: activeAbTests()
    })
    dispatch(getCartContent({ userId: user?.id, signupAnonymous }))
  }
}

export const USER_LOGOUT = 'USER_LOGOUT'
export function logoutAndResetState() {
  return async (dispatch) => {
    const apolloClient = getApolloClient()
    await apolloClient.clearStore()
    await logoutCurrentUser()
    dispatch({ type: USER_LOGOUT })
    await apolloClient.reFetchObservableQueries()
  }
}

export const changeEmailMarketingWithEclub =
  ({ eclub, consent, setSubscribed }) =>
  async () => {
    await Parse.Cloud.run('changeEmailMarketingWithEclub', { eclub, consent })
    setSubscribed(false)
  }

export const registerUserInformationRetrievalRequest = () =>
  Parse.Cloud.run('registerUserInformationRetrievalRequest')

export const updatePassword =
  ({ password }) =>
  async (dispatch) => {
    const sessionToken = await Parse.Cloud.run('updatePassword', { password })
    const user = await becomeUser(sessionToken)
    dispatch(cacheEntities(user))
    dispatch(afterLoggedInHook(user))
  }

export const getPhoneDataFromNumber = (number) => () => {
  return Parse.Cloud.run('getPhoneDataFromNumber', { phoneNumber: number })
}

export const setUserProfile = async ({ address }) => {
  const savedUser = await Parse.Cloud.run('setProfile', { address })
  setCurrentUser(savedUser)
}

export const resetFailedPayouts = ({ clearingNumber, accountNumber }) => {
  return async (dispatch) => {
    if (!internationalBankAccountFields) {
      const claimPayouts = await Parse.Cloud.run('resetFailedClaimPayouts', {
        newClearingNumber: clearingNumber,
        newAccountNumber: accountNumber
      })
      dispatch(cacheEntities(claimPayouts || []))
    }
  }
}

export const requestTokenPasswordless = ({ email, toastMessage, errorMessage }) => {
  return Parse.Cloud.run('passwordless_requestNewAuthenticationToken', { email })
    .then(() => {
      if (toastMessage)
        showToast({
          header: toastMessage.header,
          body: toastMessage.body,
          type: toastMessage.type
        })
    })
    .catch(() =>
      showToast({
        header: errorMessage.header,
        body: errorMessage.body,
        type: errorMessage.type
      })
    )
}

export const signupAnonymous = () => {
  return async (dispatch) => {
    const parseUser = getCurrentParseUser()
    if (parseUser) return parseUser
    const localStorageUserJSON = window.localStorage.getItem(
      `Parse/${Parse.applicationId}/currentUser`
    )
    const loggedInButNotInitiatedInThisBrowserTab = Boolean(localStorageUserJSON)
    const sessionToken = loggedInButNotInitiatedInThisBrowserTab
      ? JSON.parse(localStorageUserJSON).sessionToken
      : await Parse.Cloud.run('passwordless_anonymousSignup', {
          region: process.env.REGION,
          abTestFraction: getABTestFraction()
        })
    const user = await becomeUser(sessionToken)
    dispatch({ type: UPDATE_CART_ON_LOGIN })
    clearCloudCache()
    if (!loggedInButNotInitiatedInThisBrowserTab) {
      analyticsLogin('Passwordless', user.id, user)
    }
    dispatch(afterLoggedInHook(user))
    return user
  }
}

export const signUpForNewsletter = ({ email, dataUsageConsent, region }) =>
  Parse.Cloud.run('signUpForNewsletter', {
    email,
    dataUsageConsent: { ...dataUsageConsent, [PERMISSION_TYPE_EMAIL_MARKETING]: true },
    region
  })

export const signupForSellerService =
  ({ email, country }) =>
  () =>
    Parse.Cloud.run('signupForSellerService', { email, country })
      .then((response) => response)
      .catch((error) => {
        console.error(error)
        return Promise.reject(error)
      })

export const getValidatedPhoneNumber = (phoneNumber) =>
  Parse.Cloud.run('validatePhoneNumber', { phoneNumber })
